/**
 * postMessage protocol between the app and the docs it frames in the
 * full-docs dialog. Both are served from the same origin (the docs under
 * `/help/`). The docs' copy of this protocol lives in
 * `apps/docs/src/lib/appBridge.ts`; keep the two in step.
 */

export const APP_MESSAGE_SOURCE = 'kirin-app';
export const DOCS_MESSAGE_SOURCE = 'kirin-docs';

export type DocsTheme = 'light' | 'dark';

/** App → docs. */
export type AppMessage =
  | {
      source: typeof APP_MESSAGE_SOURCE;
      type: 'flags';
      flags: Record<string, boolean>;
    }
  | { source: typeof APP_MESSAGE_SOURCE; type: 'theme'; theme: DocsTheme }
  /** Open another docs page in the frame, client-side (no reload). */
  | { source: typeof APP_MESSAGE_SOURCE; type: 'navigate'; path: string }
  /** Jump to a heading in the page the docs frame shows, without a reload. */
  | { source: typeof APP_MESSAGE_SOURCE; type: 'scroll'; anchor: string };

/** Docs → app. */
export type DocsMessage =
  | { source: typeof DOCS_MESSAGE_SOURCE; type: 'ready' }
  | { source: typeof DOCS_MESSAGE_SOURCE; type: 'navigated'; path: string };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Validates a message from the docs; anything malformed is dropped. */
export function parseDocsMessage(data: unknown): DocsMessage | null {
  if (!isRecord(data) || data.source !== DOCS_MESSAGE_SOURCE) return null;
  if (data.type === 'ready') {
    return { source: DOCS_MESSAGE_SOURCE, type: 'ready' };
  }
  if (data.type === 'navigated' && typeof data.path === 'string') {
    return { source: DOCS_MESSAGE_SOURCE, type: 'navigated', path: data.path };
  }
  return null;
}

/** Only our own docs frame may talk to us: same origin, and that exact frame. */
export function isTrustedDocsEvent(
  event: MessageEvent,
  frame: HTMLIFrameElement | null
): boolean {
  return (
    !!frame?.contentWindow &&
    event.origin === window.location.origin &&
    event.source === frame.contentWindow
  );
}

/** Posts to the docs frame, pinned to our own origin. */
export function postToDocs(
  frame: HTMLIFrameElement | null,
  message: AppMessage
): void {
  frame?.contentWindow?.postMessage(message, window.location.origin);
}

/** `path` (may carry a #hash) with the app's theme as `?docusaurus-theme=`. */
export function withDocsTheme(path: string, theme: DocsTheme): string {
  const url = new URL(path, window.location.origin);
  url.searchParams.set('docusaurus-theme', theme);
  return `${url.pathname}${url.search}${url.hash}`;
}
